import * as React from "react";
import { ulid } from "ulid";
import { OpenChatWebSocketHandler } from "@/modules/open-chat/components/webSocketHandler";
import { SidebarPromptImprovementHandler } from "@/modules/open-chat/components/sidebar/sidebarPromptImprovementHandler";
import { ChatMessage } from "@/modules/open-chat/components/ChatMessage";
import { ChatInput } from "@/modules/open-chat/components/ChatInput";
import { Loader } from "@/components/ui/loader";
import { useGetMessagesQuery, useSendMessageMutation } from "@/api/open-chat";
import { OpenChatMessage } from "@/types/open-chat";
import * as analytics from "@/services/analytics";

interface Props {
  conversationId: string;
}

const emptyList: OpenChatMessage[] = [];

export const SidebarPromptImprovements = ({ conversationId }: Props) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { data: messages = emptyList, isLoading: isLoadingFirst } = useGetMessagesQuery(conversationId);
  const [sendMessageMutation, { isLoading }] = useSendMessageMutation();

  const handleSendMessage = React.useCallback(
    async (message: string) => {
      analytics.promptEnter(message);
      analytics.improvePrompt(message);
      await sendMessageMutation({
        conversationId,
        message,
        messageId: ulid(),
        prompt: "open-chat-prompt-generator",
      }).unwrap();

      if (contentRef.current !== null) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    },
    [conversationId, sendMessageMutation],
  );

  return (
    <>
      <OpenChatWebSocketHandler conversationId={conversationId} />
      <SidebarPromptImprovementHandler handleSendMessage={handleSendMessage} />

      <div className="flex flex-1 flex-col overflow-y-auto p-4" ref={contentRef}>
        <div className="flex flex-col gap-3">
          {messages.length === 0 && (
            <div className="flex flex-1 items-center justify-center">
              <div className="mx-[30px] mt-[100px] rounded-lg border border-desert-sand-400 bg-ivory-mist-100 p-3 text-center text-sm leading-relaxed text-foreground">
                <h3 className="mb-2 text-lg font-semibold">Welcome to Prompt Generator!</h3>
                <p className="text-sm text-muted-foreground">
                  Just start typing your thoughts, goals, or a stream of consciousness - no need to be precise. I'll
                  help you transform those ideas into an optimised prompt. Consider the first result our baseline; you
                  can edit it directly or keep chatting with me to refine it until it's perfect.
                </p>
              </div>
            </div>
          )}

          {messages.map(message => (
            <ChatMessage
              key={message.id}
              message={message}
              allowCopy={false}
              isImprovement={message.type === "ai"}
              hideIcons={message.type === "human"}
            />
          ))}

          {isLoading && <Loader />}
        </div>
      </div>

      <div className="border-t border-input p-4">
        <ChatInput
          placeholder="Type prompt to improve..."
          onSendMessage={handleSendMessage}
          isStreaming={false}
          isReady={!isLoading && !isLoadingFirst}
          compact
        />
      </div>
    </>
  );
};
